import { Vector3 } from 'three'
import type { Brick, BrickType } from '@models/brick'
import { BRICK_CONSTANTS, getWorldStudPositions, generateSocketPositions, getStudCounts } from '@utils/brickGeometry'

/**
 * Stud snap result containing snap information
 */
interface StudSnapResult {
  snapPosition: Vector3
  targetBrick: Brick
  snapType: 'stud' | 'grid'
}

/**
 * SnappingEngine - Handles grid-based and stud-based brick snapping
 *
 * Provides two main snapping modes:
 * 1. Grid snapping: Aligns bricks to the 0.8-unit grid on the ground plane
 * 2. Stud snapping: Snaps bricks to studs on existing bricks (vertical stacking)
 *
 * Grid Alignment:
 * - X and Z coordinates snap to multiples of 0.8 (UNIT_SIZE)
 * - Y coordinate defaults to half brick height (0.48) for ground placement
 * - Ensures all bricks align perfectly on the grid
 *
 * Stud Snapping:
 * - Detects when preview brick is hovering above existing brick studs
 * - Y = existing brick Y + BRICK_HEIGHT (0.96 units above)
 * - Snaps to stud position within STUD_SNAP_THRESHOLD (0.1 units)
 * - Validates socket alignment with studs
 */
export class SnappingEngine {
  private gridSize: number

  constructor(gridSize: number = BRICK_CONSTANTS.UNIT_SIZE) {
    this.gridSize = gridSize
  }

  /**
   * Snap a position to the nearest grid point
   *
   * CRITICAL: Grid alignment must account for brick dimensions!
   * - Odd stud counts (1x1, 1x2, 1x4): Center aligns to grid lines (0, 0.8, 1.6, ...)
   * - Even stud counts (2x2, 2x4): Center aligns to half-grid (0.4, 1.2, 2.0, ...)
   *
   * This ensures that ALL brick studs align perfectly to the 0.8-unit grid,
   * regardless of brick size.
   *
   * @param position - The 3D position to snap (typically from raycasting)
   * @param brickType - The type of brick being placed (determines snap offset)
   * @param rotation - Rotation in degrees (affects which dimension is width vs length)
   * @returns Snapped position aligned to grid (X, Z) with correct Y height
   */
  snapToGrid(position: Vector3, brickType?: BrickType, rotation: number = 0): Vector3 {
    let snappedX: number
    let snappedZ: number

    if (brickType) {
      // Get stud counts for this brick type
      let { studsX, studsZ } = getStudCounts(brickType)

      // Account for rotation: 90° or 270° rotations swap X and Z dimensions
      if (rotation === 90 || rotation === 270) {
        [studsX, studsZ] = [studsZ, studsX]
      }

      // Calculate snap offsets based on stud counts
      // Odd stud count: offset = 0 (snap to grid lines)
      // Even stud count: offset = gridSize/2 (snap to half-grid)
      const offsetX = (studsX % 2 === 0) ? this.gridSize / 2 : 0
      const offsetZ = (studsZ % 2 === 0) ? this.gridSize / 2 : 0

      // Snap to grid with appropriate offset
      snappedX = Math.round((position.x - offsetX) / this.gridSize) * this.gridSize + offsetX
      snappedZ = Math.round((position.z - offsetZ) / this.gridSize) * this.gridSize + offsetZ
    } else {
      // Fallback: snap to regular grid (for 1x1 bricks or unknown types)
      snappedX = Math.round(position.x / this.gridSize) * this.gridSize
      snappedZ = Math.round(position.z / this.gridSize) * this.gridSize
    }

    // For ground placement, Y is half the brick height (center of brick at correct position)
    const snappedY = BRICK_CONSTANTS.BRICK_HEIGHT / 2

    return new Vector3(snappedX, snappedY, snappedZ)
  }

  /**
   * Find nearby bricks within a certain radius of the position
   *
   * @param position - The position to check
   * @param radius - Search radius
   * @param allBricks - All placed bricks
   * @returns Array of bricks within radius
   */
  findNearbyBricks(position: Vector3, radius: number, allBricks: Brick[]): Brick[] {
    return allBricks.filter(brick => {
      const distance = Math.sqrt(
        Math.pow(brick.position.x - position.x, 2) +
        Math.pow(brick.position.z - position.z, 2)
      )
      return distance <= radius
    })
  }

  /**
   * Attempt to snap to studs on a nearby brick
   *
   * Checks if the preview position is hovering above a brick's studs.
   * If within threshold, snaps to the exact stud position for vertical stacking.
   *
   * @param position - The current preview position
   * @param nearbyBricks - Bricks to check for stud snapping
   * @param previewType - Type of brick being placed
   * @param previewRotation - Rotation of preview brick
   * @returns StudSnapResult if snap found, null otherwise
   */
  trySnapToStuds(
    position: Vector3,
    nearbyBricks: Brick[],
    _previewType: string,
    _previewRotation: number
  ): StudSnapResult | null {
    const { BRICK_HEIGHT, STUD_SNAP_THRESHOLD } = BRICK_CONSTANTS

    for (const brick of nearbyBricks) {
      // Get world-space stud positions for this brick
      const studPositions = getWorldStudPositions(brick.type, brick.position, brick.rotation)

      // Check each stud on the brick
      for (const studPos of studPositions) {
        // Calculate where the preview brick center would be if placed on this stud
        const stackedY = brick.position.y + BRICK_HEIGHT

        // Check if preview position is near this stud horizontally
        const horizontalDistance = Math.sqrt(
          Math.pow(position.x - studPos.x, 2) +
          Math.pow(position.z - studPos.z, 2)
        )

        // Check if preview position is at the right height (above the brick)
        const verticalDistance = Math.abs(position.y - stackedY)

        // If preview is hovering near this stud both horizontally and vertically
        // Note: When hovering over brick top surface, intersection point is at brick.y + height/2,
        // but we want to stack at brick.y + height. So vertical threshold must be >= height/2 (0.48)
        // Horizontal threshold should cover the entire brick top surface (half width = 0.4 units)
        const { UNIT_SIZE } = BRICK_CONSTANTS
        const horizontalThreshold = UNIT_SIZE * 0.6 // 0.48 units - allows hovering anywhere on brick top
        if (
          horizontalDistance < horizontalThreshold &&
          verticalDistance < BRICK_HEIGHT // Allow up to one brick height of vertical deviation
        ) {
          // Snap to this stud position
          // Center the preview brick so its socket aligns with this stud
          const snapPosition = new Vector3(studPos.x, stackedY, studPos.z)

          return {
            snapPosition,
            targetBrick: brick,
            snapType: 'stud',
          }
        }
      }
    }

    return null
  }

  /**
   * Find valid snap point considering nearby bricks
   *
   * Priority:
   * 1. Try to snap to studs on nearby bricks (vertical stacking)
   * 2. Fall back to grid snapping (ground placement)
   *
   * @param position - The mouse position in 3D space
   * @param allBricks - All placed bricks for stud detection
   * @param previewType - Type of brick being placed
   * @param previewRotation - Rotation of preview brick
   * @returns Valid snap position and metadata
   */
  findSnapPoint(
    position: Vector3,
    allBricks: Brick[] = [],
    previewType: string = '1x1',
    previewRotation: number = 0
  ): StudSnapResult {
    // Find bricks near the cursor position
    const searchRadius = 3.0 // Search within 3 units
    const nearbyBricks = this.findNearbyBricks(position, searchRadius, allBricks)

    // Try to snap to studs on nearby bricks
    if (nearbyBricks.length > 0) {
      const studSnap = this.trySnapToStuds(position, nearbyBricks, previewType, previewRotation)
      if (studSnap) {
        return studSnap
      }
    }

    // Fall back to grid snapping (pass brick type and rotation for proper alignment)
    const gridPosition = this.snapToGrid(position, previewType as BrickType, previewRotation)
    return {
      snapPosition: gridPosition,
      targetBrick: null as any, // No target brick for grid placement
      snapType: 'grid',
    }
  }

  /**
   * Validate if a connection between two bricks is valid
   *
   * Checks if the preview brick's sockets align with the target brick's studs.
   * Used for additional validation beyond proximity-based snapping.
   *
   * @param previewBrick - The brick being placed
   * @param targetBrick - The brick below (with studs)
   * @returns true if connection is valid
   */
  validateConnection(previewBrick: Brick, targetBrick: Brick): boolean {
    // Get socket positions on preview brick (bottom)
    const socketPositions = generateSocketPositions(
      previewBrick.type,
      previewBrick.rotation
    )

    // Get stud positions on target brick (top)
    const studPositions = getWorldStudPositions(
      targetBrick.type,
      targetBrick.position,
      targetBrick.rotation
    )

    // Convert preview sockets to world space
    const worldSockets = socketPositions.map(local =>
      new Vector3(
        previewBrick.position.x + local.x,
        previewBrick.position.y + local.y,
        previewBrick.position.z + local.z
      )
    )

    // Check if at least one socket aligns with a stud
    for (const socket of worldSockets) {
      for (const stud of studPositions) {
        const distance = Math.sqrt(
          Math.pow(socket.x - stud.x, 2) +
          Math.pow(socket.z - stud.z, 2)
        )

        if (distance < BRICK_CONSTANTS.STUD_SNAP_THRESHOLD) {
          return true // At least one connection point
        }
      }
    }

    return false // No valid connections
  }
}
